/**
 * @file     worker/management/unclaimed-earnings.js - utility functions related to the implementation
 *           of the unclaimed earnings feature.
 * @author   Wes Garland, wes@distributive.network
 * @date     July 2025
 *
 * @description
 * Workers that are started without specifying a payment address in the config object will use a payment
 * address obtained from local storage (if available) or dynamically created (and then stored) on the
 * spot. This allows users to begin earning credits immediately, and then claim their credits at a later
 * date. This means we need to store the private key for the earnings account locally until the worker
 * is registered. At registration time, we use this key to transfer the funds after adding the correct
 * key to the worker's configuration to use the registration-specified payment account.
 *
 * Concept note: unclaimed funds are per "machine", not per "worker instance". Really, "machine" is the
 * local storage scope.  This should make it easier to avoid losing funds when troubleshooting a
 * malfunctioning worker.  And that is why the related methods are attached to DistributiveWorker
 * instead of a worker instance.  Note also that unclaimed funds are also per scheduler. This makes it
 * safe to delete the unclaimed earnings keystore without worrying about losing funds from another
 * scheduler.
 */
'use strict';

const wallet = require('dcp/dcp-client/wallet');

const { localStorage }     = require('dcp/common/dcp-localstorage');
const { localStorageKeys } = require('../consts');
const { Connection }       = require('dcp/protocol');
const { BigNumber }        = require('dcp/types');
const { assert }           = require('dcp/common/dcp-assert');

const bnZero = new BigNumber(0);

function getKs()
{
  return false
    || localStorage.getItem(localStorageKeys.worker.uncEarnPrefix + dcpConfig.scheduler.location.href)
    || localStorage.getItem(localStorageKeys.worker.uncEarnObsolete);
}

function setKs(ks)
{
  return localStorage.setItem(localStorageKeys.worker.uncEarnPrefix + dcpConfig.scheduler.location.href, JSON.stringify(ks));
}

/**
 * Figure out where unclaimed earnings are stored on this computer.
 *
 * @returns { Address }    the address of that bank account
 */
async function obtainUnclaimedEarningsPaymentAddress(worker)
{
  var ks, ksStr = getKs()

  if (ksStr)
    ks = await new wallet.BankAccountKeystore(ksStr);
  else
  {
    ks = await new wallet.BankAccountKeystore(null, '');  /* now the race is on */
    ks.label = 'unclaimed earnings';
    ksStr = getKs();
    if (ksStr)
      ks = await new wallet.BankAccountKeystore(ksStr);
    else
      setKs(ks);
  }

  return ks.address;
}

/**
 * @returns {BigNumber}   the number of unclaimed credits in the unclaimed earnings bank account
 *                        associated with this computer/browser
 */
async function checkUnclaimedEarnings()
{
  const ksJSON = getKs();
  if (!ksJSON)
    return bnZero;
  const accountKs = await new wallet.BankAccountKeystore(ksJSON);
  const bankTeller = new Connection(dcpConfig.bank.services.bankTeller);

  try
  {
    const req = new bankTeller.Request('viewAccounts', {
      addresses: [ accountKs.address ],
    });
    req.authorize(accountKs);
    const res = await req.send();

    if (!res.success)
    {
      const error = new Error(`cannot check balance of unclaimed earnings account ${accountKs}`);
      if (res.payload instanceof bankTeller.ErrorPayload)
      {
        error.message += ` (${res.payload.message})`;
        throw error;
      }
    }
    return res.payload.accounts[0].balance;
  }
  finally
  {
    bankTeller.close();
  }
}

/**
 * @param {string | Address}   toAccount    the account into which to deposit unclaimed earnings
 * @returns {BigNumber} the number of credits deposited
 */
async function transferUnclaimedEarnings(toAccount)
{
  const ksJSON = getKs();
  if (!ksJSON)
    return { success: true, payload: bnZero };
  const bankTeller = new Connection(dcpConfig.bank.services.bankTeller);
  const fromAccountKs = await new wallet.Keystore(ksJSON);

  if (typeof toAccount === 'string')
    toAccount = new wallet.Address(toAccount);
  assert(toAccount instanceof wallet.Address);

  try
  {
    const req = new bankTeller.Request('transfer@1', {
      fromAccount: fromAccountKs.address,
      toAccount:   toAccount,
      transferAll: true,
    });
    await req.authorize(fromAccountKs);

    const res = await req.send();
    if (res.success === true)
      localStorage.removeItem(localStorageKeys.worker.uncEarnPrefix + dcpConfig.scheduler.location.href);

    return res;
  }
  finally
  {
    bankTeller.close();
  }
}

exports.transferUnclaimedEarnings = transferUnclaimedEarnings;
exports.checkUnclaimedEarnings = checkUnclaimedEarnings;
exports.obtainUnclaimedEarningsPaymentAddress = obtainUnclaimedEarningsPaymentAddress;
